import { Component, OnInit, AfterViewInit, OnDestroy, ChangeDetectionStrategy, signal, computed, ChangeDetectorRef, ViewChild, ElementRef, ViewEncapsulation } from '@angular/core';

import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { Util } from 'src/shared/util/util';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { StripeProvider } from 'src/providers/stripe/stripe-provider';
import { ThemeService } from 'src/shared/services/theme.service';
import { Card } from 'src/providers/card/card';
import { PaymentMethodsProvider } from 'src/providers/payment-methods/payment-methods.provider';
import { GuestReservations } from 'src/providers/guest-reservations/guest-reservations';
import { Requests } from 'src/providers/requests/requests';
import { BookJob } from 'src/providers/book-job/book-job';
import { AgentsProvider } from 'src/providers/agents/agents';
import { Workflows } from 'src/providers/workflows/workflows';
import { Client } from 'src/providers/client/client';
import { OnboardingProvider } from 'src/providers/onboarding/onboarding.provider';
import { Me } from 'src/providers/me/me';
import { Events } from 'src/providers/events/events';
import { WindowService } from 'src/shared/providers/window.service';
import { SubscriptionsQueriesService } from 'src/shared/services/subscriptions-queries.service';
import { FeatureGateService } from 'src/shared/services/feature-gate.service';
import { CLEANING_MAINTENANCE_PLAN_KEY, FULL_AUTOPILOT_PLAN_KEY, SubscriptionGateService } from 'src/shared/services/subscription-gate.service';
import { DemoAccountService } from 'src/shared/services/demo-account.service';
import { SuccessPage, SuccessPageParamsModel } from 'src/shared/pages/success/success';
import { BASE_MINIMUM_PER_UNIT, CLEANING_MAINTENANCE_PER_UNIT } from 'src/shared/components/subscription-plan-card/subscription-plan-card.component';

interface AutopilotCheckItem {
  id: string;
  label: string;
  status: 'complete' | 'incomplete' | 'warning';
  description?: string;
}

interface AutopilotState {
  cardAdded: boolean;
  cardBrand?: string;
  cardLast4?: string;
  callDecisionMade?: boolean;
}

interface PendingAutopilotAction {
  type: 'automatic-booking' | 'job-request' | 'agent-enable' | 'workflow-publish' | 'workflow-enable';
  payload: any;
  successMessage: string;
  successRoute: string;
}

// How far back a retry looks for the subscribe charge it may already have paid. Charges carry
// only a UTC date, so this only matters in the first hours after 00:00 UTC.
const SUBSCRIBE_RETRY_LOOKBACK_MS = 6 * 60 * 60 * 1000;

@Component({
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  templateUrl: './enable-autopilot.component.html',
  styleUrls: ['./enable-autopilot.component.scss']
})
export class EnableAutopilotComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('cardInfo', { static: false }) cardInfo!: ElementRef;

  // Mock automation percentage (in production, would be calculated from actual data)
  automationPercentage = signal(35);

  // True until we've confirmed the account isn't already on a Full Autopilot plan. The form
  // (and the Stripe element) only renders once this clears, so a subscribed client can never
  // be shown — or charged by — the subscribe form again, whichever way they got here.
  isCheckingSubscription = signal(true);

  cardAdded = signal(false);
  cardBrand = signal('');
  cardLast4 = signal('');
  isAddingCard = signal(false);
  stripeReady = signal(false);
  callDecisionMade = signal(false);
  hasExistingCard = signal(false);
  existingCardLast4 = signal('');
  isEditingCard = signal(false);
  errorMessage = '';
  addressCount = signal(0);
  fromGettingStarted = false;
  // Set when opened via the concierge-assigned "Subscribe" onboarding step: shows a plain
  // "Subscribe" header and closes on success instead of forwarding to a pending gated step.
  private fromSubscribeStep = false;

  cleaningMaintenanceOn = signal(true);
  // Accounts with no addresses yet still see real per-unit numbers.
  unitCount = computed(() => Math.max(this.addressCount(), 1));
  baseMinimumDollars = computed(() => this.unitCount() * BASE_MINIMUM_PER_UNIT);
  cleaningMaintenanceDollars = computed(() => this.unitCount() * CLEANING_MAINTENANCE_PER_UNIT);
  // Charged at signup as account credit ("skin in the game" — see tidy.com/pricing "How
  // getting started works"): the first month's minimum without C&M (stays as credit toward
  // booking fees), or the first month of C&M with it (consumed by the plan's $39/unit debit).
  dueTodayDollars = computed(() =>
    this.cleaningMaintenanceOn() ? this.cleaningMaintenanceDollars() : this.baseMinimumDollars()
  );

  checklistItems = signal<AutopilotCheckItem[]>([
    {
      id: 'pros-added',
      label: 'Pros Added',
      status: 'complete',
      description: '3 pros on your priority list'
    },
    {
      id: 'workflows-configured',
      label: 'Workflows Configured',
      status: 'complete',
      description: '2 workflows created'
    },
    {
      id: 'checklists-created',
      label: 'Checklists Created',
      status: 'warning',
      description: '1 checklist (recommend at least 2)'
    },
    {
      id: 'integrations-connected',
      label: 'Integrations Connected',
      status: 'complete',
      description: 'Airbnb calendar synced'
    }
  ]);

  // Set once the due-today charge is known to have gone through; a later Subscribe tap skips the card and the charge.
  dueTodayCharged = signal(false);
  private cardEl: any;
  private cardHandler = this.onChange.bind(this);
  pendingAction: PendingAutopilotAction | null = null;
  private pendingStep: any = null;

  constructor(
    private rightSidePanel: RightSidePanelService,
    private storage: TidyStorage,
    private cdr: ChangeDetectorRef,
    private util: Util,
    private navCtrl: CustomNavController,
    private stripeProvider: StripeProvider,
    private themeService: ThemeService,
    private card: Card,
    private paymentMethodsProvider: PaymentMethodsProvider,
    private guestReservations: GuestReservations,
    private requests: Requests,
    private bookJob: BookJob,
    private agentsProvider: AgentsProvider,
    private workflowProvider: Workflows,
    private client: Client,
    private onboardingProvider: OnboardingProvider,
    private events: Events,
    public windowService: WindowService,
    private me: Me,
    private subscriptionsQueries: SubscriptionsQueriesService,
    private featureGate: FeatureGateService,
    private subscriptionGate: SubscriptionGateService
  ) {}

  async ngOnInit() {
    this.rightSidePanel.setDialogPageTitle('Subscribe');
    await this.loadDialogParams();
    // Already on a Full Autopilot plan? Then there is nothing to subscribe to: swap the page
    // for an "already subscribed" prompt instead of the payment form. This covers every way
    // in — the concierge `?next=subscribe` link, the Getting Started "Subscribe" item, the
    // feature gates and the Subscription page — and reads the API directly so a stale cache
    // can't re-open the form right after a successful subscribe (Trello 18574).
    if (await this.subscriptionGate.hasFullAutopilotSubscription()) {
      await this.showAlreadySubscribedPrompt();
      return;
    }
    this.isCheckingSubscription.set(false);
    this.cdr.markForCheck();
    this.loadState();
    this.loadPendingAction();
    this.loadAddresses();
    this.checkExistingCard();
    // QA-only: holding the dialog X for 5s reveals "Skip", which bypasses the subscription
    // and proceeds straight to the gated step the user was trying to reach.
    this.rightSidePanel.customSkipAction$.next(() => this.qaSkip());
  }

  // Replaces the subscribe page with the standard success prompt. OK closes the dialog on
  // desktop (the client is already on the page they came from) and returns to the dashboard
  // on mobile, where the page is a full-screen route.
  private async showAlreadySubscribedPrompt(): Promise<void> {
    await this.storage.delete('autopilot-pending-step');
    await this.storage.delete('autopilot-pending-action');
    await this.storage.delete('autopilot-demo-state');
    const params: SuccessPageParamsModel = {
      header: 'You\'re already subscribed',
      body: 'Your account already has an active Full Autopilot subscription, so there\'s nothing more to do here.',
      buttonText: 'OK',
      buttonRoute: 'properties',
      allButtonsPrimary: true,
    };
    if (this.windowService.isDesktopRes) {
      params.customActionKey = 'closeRightSidePanel';
    }
    this.rightSidePanel.setDialogPageTitle('Already Subscribed');
    await this.rightSidePanel.navigateTo('success', params, SuccessPage);
  }

  // After a successful subscribe, leave the page. `closeRightSidePanel()` is a no-op on mobile
  // (there is no dialog — the page is a full-screen route), which used to leave the filled-in
  // form and a live "Subscribe Now" button on screen, inviting a second payment.
  private async leaveAfterSubscribe(): Promise<void> {
    if (this.windowService.isDesktopRes) {
      this.rightSidePanel.closeRightSidePanel();
      return;
    }
    await this.navCtrl.navigateRoot(this.fromGettingStarted ? 'getting-started' : 'properties');
  }

  // QA-only skip: go to the pending (gated) step without subscribing, or close if none.
  qaSkip = async () => {
    if (this.pendingStep) {
      await this.storage.delete('autopilot-pending-step');
      const dialogParams = await this.storage.retrieve('dialog-params') || {};
      dialogParams.cameFromSubscribePage = true;
      await this.storage.save('dialog-params', dialogParams);
      this.rightSidePanel.setDialogPageTitle(this.pendingStep.title);
      this.rightSidePanel.navigateTo(this.pendingStep.route, { ...this.pendingStep.params, cameFromSubscribePage: true });
      return;
    }
    this.rightSidePanel.closeRightSidePanel();
  };

  async loadPendingAction() {
    this.pendingAction = await this.storage.retrieve('autopilot-pending-action') as PendingAutopilotAction;
    this.pendingStep = await this.storage.retrieve('autopilot-pending-step');
  }

  private async loadDialogParams() {
    const params = await this.storage.retrieve('dialog-params') as { fromGettingStarted?: boolean; fromSubscribeStep?: boolean } | null;
    this.fromGettingStarted = !!params?.fromGettingStarted;
    this.fromSubscribeStep = !!params?.fromSubscribeStep;
    this.cdr.markForCheck();
  }

  private async loadAddresses(): Promise<void> {
    try {
      const addresses = await this.client.getAddresses();
      this.addressCount.set(addresses?.length || 0);
      this.cdr.markForCheck();
    } catch (error) {
      this.addressCount.set(0);
      this.cdr.markForCheck();
    }
  }

  // WiFi + Noise Sensor and The Box aren't self-serve yet — the toggle never turns on,
  // the tap just shows the "contact your account manager" prompt.
  gateHardwareAddOn() {
    this.featureGate.gate();
  }

  ngAfterViewInit() {
    // Stripe mounting is handled by checkExistingCard() or onEditCard()
  }

  ngOnDestroy() {
    this.rightSidePanel.customSkipAction$.next(null);
    if (this.cardEl) {
      this.cardEl.removeEventListener('change', this.cardHandler);
      this.cardEl.destroy();
      this.cardEl = null;
    }
  }

  private mountStripeForm(): void {
    this.stripeReady.set(false);
    if (this.cardEl) {
      this.cardEl.removeEventListener('change', this.cardHandler);
      this.cardEl.destroy();
      this.cardEl = null;
    }
    setTimeout(() => {
      if (!this.cardInfo) {return;}
      this.cardEl = this.stripeProvider.getCardElement(!this.themeService.isDarkMode());
      this.cardEl.mount(this.cardInfo.nativeElement);
      this.cardEl.addEventListener('change', this.cardHandler);
      this.cardEl.on('ready', () => {
        this.stripeReady.set(true);
        this.cdr.markForCheck();
      });
    }, 100);
  }

  private async checkExistingCard(): Promise<void> {
    try {
      const cards = await this.card.getCreditCards();
      const primaryCard = cards.find((c: any) => c.is_primary) || cards[0];
      if (primaryCard?.metadata?.last4) {
        this.hasExistingCard.set(true);
        this.existingCardLast4.set(primaryCard.metadata.last4);
        this.cdr.markForCheck();
        return;
      }
    } catch (error) {
      // Fall through to mount Stripe form
    }
    // A bank account is deliberately NOT accepted here, even though it can pay the
    // due-today charge: billing_api can only ever set a CARD as the billing account's
    // default payment method (payment_method/services/create.rb + set_tidy_account.rb
    // both gate on `payment_method.card?`), and the recurring sweep charges exactly that
    // field — it skips accounts without one entirely. An ACH-only subscriber would pay
    // once, then silently accrue a negative balance until the 22-day sweep suspends them
    // and force-downgrades the plan to flex. So ACH-only clients get the card form.
    this.hasExistingCard.set(false);
    this.cdr.markForCheck();
    this.mountStripeForm();
  }

  onEditCard(): void {
    this.isEditingCard.set(true);
    this.cdr.markForCheck();
    this.mountStripeForm();
  }

  onChange(err: any): void {
    this.errorMessage = err.error ? err.error.message : '';
    this.cdr.markForCheck();
  }

  async loadState() {
    const state = await this.storage.retrieve('autopilot-demo-state') as AutopilotState;
    if (state?.cardAdded) {
      this.cardAdded.set(true);
      this.cardBrand.set(state.cardBrand || '');
      this.cardLast4.set(state.cardLast4 || '');
    }
    if (state?.callDecisionMade) {
      this.callDecisionMade.set(true);
    }
  }

  getStatusIcon(status: string): string {
    const icons: Record<string, string> = {
      complete: 'assets/img/checkmark-green.svg',
      incomplete: 'assets/img/circle-outline.svg',
      warning: 'assets/img/alert.svg'
    };
    return icons[status] || icons.incomplete;
  }

  async skipCall() {
    this.callDecisionMade.set(true);
    await this.saveDecisionState();
    this.cdr.markForCheck();
  }

  private async saveDecisionState() {
    const existingState = await this.storage.retrieve('autopilot-demo-state') as AutopilotState || {};
    await this.storage.save('autopilot-demo-state', {
      ...existingState,
      callDecisionMade: true
    } as AutopilotState);
  }

  async goLive() {
    try {
      this.isAddingCard.set(true);
      this.errorMessage = '';
      this.cdr.markForCheck();

      let paymentMethodId: string | null = null;

      // If card not already added and no existing card (or user is editing), create and save it first
      const shouldCreateNewCard = !this.dueTodayCharged() && !this.cardAdded() && (!this.hasExistingCard() || this.isEditingCard());
      if (shouldCreateNewCard) {
        // Create Stripe token
        const { token, error } = await this.stripeProvider
          .getInstance()
          .createToken(this.cardEl);

        if (error) {
          this.showSubscribeError(error, 'Error creating card token');
          return;
        }

        if (!token) {
          this.showSubscribeError(null, 'Failed to create card token');
          return;
        }

        // Save card to backend
        const savedCard: any = await this.card.saveCard({
          type: 'card',
          stripe_card_token: token.id,
          primary_payment_method: true,
        });
        paymentMethodId = savedCard?.id || null;
      }

      // Charge today's amount as account credit (same endpoint as load-credit). Without
      // C&M the credit just sits against future 3.9% booking fees; with C&M the plan's
      // first $39/unit cycle debit consumes it. Ongoing billing is all backend-side.
      if (!this.dueTodayCharged() && this.dueTodayDollars() > 0) {
        if (!paymentMethodId) {
          const paymentMethods = await this.paymentMethodsProvider.getPaymentMethods(true);
          const cards = (paymentMethods || []).filter((pm: any) => pm?.type === 'card');
          const primary = cards.find((pm: any) => pm?.is_primary || pm?.is_prmary) || cards[0];
          paymentMethodId = primary?.id || null;
        }

        const amountInCents = Math.round(this.dueTodayDollars() * 100) - await this.subscribeChargesPaidToday();
        if (amountInCents <= 0) {
          this.dueTodayCharged.set(true);
        } else if (paymentMethodId) {
          await this.paymentMethodsProvider.addCredit(paymentMethodId, amountInCents);
          this.dueTodayCharged.set(true);
        }
      }

      // Subscribe to the chosen plan — recurring billing (3.9% of reservations at checkout,
      // $19/unit monthly minimum, plus $39/unit/mo when C&M is on) is handled by the backend.
      const planTypeKey = this.cleaningMaintenanceOn() ? CLEANING_MAINTENANCE_PLAN_KEY : FULL_AUTOPILOT_PLAN_KEY;
      await this.me.changeSubscription({ plan_type_key: planTypeKey });
      this.subscriptionsQueries.invalidate();

      // Set hasCard flag
      localStorage.setItem('hasCard', 'true');

      // The subscription applies account-wide, so mark the client-locked "Subscribe" onboarding
      // step complete for every address. This flips the account-manager checkmark no matter how
      // the user subscribed — via the step itself, the paywall, or the /subscribe link.
      await this.markSubscribeStepCompleted();

      // Concierge-assigned "Subscribe" onboarding step: close rather than forwarding to a
      // (possibly stale) pending gated step.
      if (this.fromSubscribeStep) {
        await this.storage.delete('autopilot-demo-state');
        this.util.showSuccess('Successfully subscribed.');
        await this.leaveAfterSubscribe();
        this.isAddingCard.set(false);
        this.cdr.markForCheck();
        return;
      }

      // Navigate to pending onboarding step if exists
      if (this.pendingStep) {
        await this.storage.delete('autopilot-pending-step');
        await this.storage.delete('autopilot-demo-state');
        const dialogParams = await this.storage.retrieve('dialog-params') || {};
        dialogParams.cameFromSubscribePage = true;
        await this.storage.save('dialog-params', dialogParams);
        this.rightSidePanel.setDialogPageTitle(this.pendingStep.title);
        this.rightSidePanel.navigateTo(this.pendingStep.route, { ...this.pendingStep.params, cameFromSubscribePage: true });
        this.isAddingCard.set(false);
        this.cdr.markForCheck();
        return;
      }

      // Execute pending action if exists
      if (this.pendingAction) {
        await this.executePendingAction();
        return;
      }

      // Show success message
      this.util.showSuccess('Successfully subscribed.');

      // Clean up state and leave the page
      await this.storage.delete('autopilot-demo-state');
      await this.leaveAfterSubscribe();

      this.isAddingCard.set(false);
      this.cdr.markForCheck();
    } catch (error: any) {
      this.showSubscribeError(error, 'An error occurred');
    }
  }

  // What the account has already paid toward today's subscribe charge, in cents: today's
  // unrefunded charges of either plan's due-today amount, or the difference between them. A
  // charge whose answer was lost (it went through after the request timed out, or the client
  // left and came back, maybe with the other plan picked) counts, so only what's left is charged.
  // Billing dates charges in UTC, with no time. In the first hours of a UTC day (8 pm Eastern on)
  // yesterday's date counts too, so a retry that crosses 00:00 UTC still finds the charge.
  private async subscribeChargesPaidToday(): Promise<number> {
    const charges = (await this.client.getCustomerCharges()) as any[];
    const now = Date.now();
    const chargeDays = [now, now - SUBSCRIBE_RETRY_LOOKBACK_MS].map((ms) => new Date(ms).toISOString().slice(0, 10));
    const baseCents = Math.round(this.baseMinimumDollars() * 100);
    const cleaningMaintenanceCents = Math.round(this.cleaningMaintenanceDollars() * 100);
    const subscribeAmounts = [baseCents, cleaningMaintenanceCents, cleaningMaintenanceCents - baseCents];
    return (charges || [])
      .filter((charge: any) =>
        subscribeAmounts.includes(charge?.amount)
        && ['succeeded', 'processing'].includes(charge?.status)
        && chargeDays.includes(String(charge?.date || '').slice(0, 10))
      )
      .reduce((paid: number, charge: any) => paid + Math.max(charge.amount_left_to_refund ?? charge.amount, 0), 0);
  }

  // Surfaces a failed subscribe attempt as a toast as well as inline. The inline
  // <tidy-error-message> lives with the card form, which isn't rendered when the account
  // already has a card on file — and that is exactly when the likeliest failure, a declined
  // charge, happens. Setting only `errorMessage` swallowed it: the button spun, reset, and
  // the client was left with no idea their card was declined.
  // `err.error.message` is the API's own wording (Stripe's "Your card has insufficient
  // funds." reaches us verbatim); `err.message` covers HttpClientCustom's rejection shape
  // and the Stripe.js token errors.
  private showSubscribeError(error: any, fallback: string): void {
    const message = error?.error?.message || error?.message || fallback;
    this.errorMessage = message;
    this.util.showError(message, 10000);
    this.isAddingCard.set(false);
    this.cdr.markForCheck();
  }

  // A successful subscription is account-wide, so flip the "Subscribe" onboarding step to
  // completed for every address — plus an account-level row, so a client who subscribes
  // before adding any property (or adds one later) isn't asked again. Best-effort: a
  // failure here never blocks the subscription.
  private async markSubscribeStepCompleted(): Promise<void> {
    try {
      const addresses = await this.client.getAddresses();
      await Promise.all([
        this.client.addMetadata({ key: DemoAccountService.SUBSCRIBE_ACCOUNT_META_KEY, value: 'completed' }),
        ...(addresses || []).map((address: any) =>
          this.client.addMetadata({
            key: `onboarding_step.${address.id}.subscribe`,
            value: 'completed',
          })
        ),
      ]);
    } catch (_err) {
      // Non-fatal: the subscription succeeded even if we couldn't flip the checkmark.
    }
  }

  private async executePendingAction(): Promise<void> {
    try {
      const { type, payload, successMessage, successRoute } = this.pendingAction;

      switch (type) {
        case 'automatic-booking':
          await this.guestReservations.updateManyAddressAutomaticBooking(payload.automaticBookingPayload);
          await this.onboardingProvider.markGoalAsCompleted('enableAutomaticBooking');
          this.onboardingProvider.setShowOnboardingOnPage(false);
          this.onboardingProvider.setShowOnboardingInHeader(true);
          await this.client.getAddresses(true);
          await this.storage.save('shouldReloadAllAutomations', true);
          break;

        case 'job-request':
          if (payload.requestType === 'assign') {
            await this.bookJob.saveJob(payload.bookingType, payload.jobPayload, null, null);
          } else if (payload.requestType === 'all_my_pros') {
            await this.requests.addJobRequest(payload.addressId, payload.jobPayload);
          } else {
            await this.requests.addProRequest(payload.addressId, payload.jobPayload);
          }
          localStorage.setItem('madeUpdateToSchedule', 'true');
          localStorage.setItem('didBookJob', 'true');
          break;

        case 'agent-enable':
          await this.agentsProvider.updateAgentSettings(payload.agentKey, payload.agentPayload);
          const agentSettings = await this.agentsProvider.getAgentSettings();
          const updatedAgentSettings = agentSettings.map(agent =>
            agent.key === payload.agentKey ? payload.agentPayload : agent
          );
          await this.storage.save('agentSettings', updatedAgentSettings);
          this.events.publish('agent:settings:updated');
          if (payload.onboardingFlowName) {
            await this.onboardingProvider.markGoalAsCompleted(payload.onboardingFlowName);
          }
          break;

        case 'workflow-publish':
          await this.workflowProvider.publishWorkflow(payload.versionId);
          await this.workflowProvider.unpauseWorkflow(payload.workflowId);
          break;

        case 'workflow-enable':
          await this.workflowProvider.unpauseWorkflow(payload.workflowId);
          break;
      }

      // Clean up
      await this.storage.delete('autopilot-pending-action');
      await this.storage.delete('autopilot-demo-state');

      // Show success and navigate
      this.util.showSuccess(successMessage);
      this.rightSidePanel.closeRightSidePanel();
      this.navCtrl.navigateForward(successRoute);

      this.isAddingCard.set(false);
      this.cdr.markForCheck();
    } catch (error: any) {
      this.showSubscribeError(error, 'An error occurred while completing the action');
    }
  }

}
